import { formatPrice, formatRange } from "@/lib/currency";
import { Wrench, Sparkles, Check } from "lucide-react";

export default function Ongoing({ currency, rates }) {
  return (
    <section data-testid="ongoing-section" className="border-t border-[#E5E0D8] bg-white">
      <div className="oak-container oak-section">
        <div className="max-w-3xl">
          <p className="text-sm uppercase tracking-[0.18em] text-[#5C665F]">The Oakbridg promise</p>
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight mt-3 text-[#1A3622]">
            Never think about your website again.
          </h2>
          <p className="mt-5 text-[17px] md:text-xl leading-relaxed text-[#5C665F]">
            Most of our clients don’t just want a website — they want the website *handled*. Pay
            us {formatPrice(25, currency, rates)} a month and Oakbridg quietly keeps everything
            running behind the scenes. No dashboards to log into. No developers to chase.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-[1.6fr_1fr] gap-5">
          {/* Maintenance — flagship card */}
          <div
            data-testid="maintenance-card"
            className="relative rounded-2xl border border-[#1A3622] bg-[#1A3622] text-white p-8 md:p-12 shadow-lg overflow-hidden"
          >
            <span
              data-testid="maintenance-recommended-badge"
              className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-full bg-[#C17F59] text-white text-[11px] uppercase tracking-[0.15em] px-3 py-1"
            >
              Most clients choose this
            </span>

            <div className="w-12 h-12 rounded-full bg-[#C17F59] text-white flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>

            <h3 className="font-heading font-bold text-2xl md:text-3xl mt-6 text-white">
              Managed maintenance
            </h3>

            <p className="font-heading font-extrabold tracking-tight text-5xl md:text-6xl mt-4 text-white">
              {formatPrice(25, currency, rates)}
              <span className="font-body font-normal text-lg text-white/70"> /month</span>
            </p>

            <p className="mt-4 text-[16px] leading-relaxed text-white/80 max-w-lg">
              A flat monthly fee. We handle everything technical so you can get on with the work
              that actually pays.
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[15px] text-white/90 max-w-xl">
              {[
                "Hosting kept fast & up",
                "Security updates & backups",
                "Uptime monitoring 24/7",
                "SSL certificate renewals",
                "Small tweaks (under 15 min)",
                "Priority email support",
                "Yearly domain renewal reminders",
                "No dashboards to learn",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 mt-1 shrink-0 text-[#C17F59]" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p className="mt-8 text-[13px] text-white/60">
              Included with every new build. Truly hands-off.
            </p>
          </div>

          {/* Modifications */}
          <div
            data-testid="modifications-card"
            className="rounded-2xl border border-[#E5E0D8] bg-[#FAF9F6] p-8 md:p-10 transition-shadow hover:shadow-md flex flex-col"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="w-11 h-11 rounded-full bg-[#1A3622] text-white flex items-center justify-center">
                <Wrench className="w-5 h-5" />
              </div>
              <span className="text-xs uppercase tracking-[0.16em] text-[#5C665F]">Per change</span>
            </div>
            <h3 className="font-heading font-bold text-2xl mt-6 text-[#1A3622]">
              Bigger changes
            </h3>
            <p className="font-heading font-extrabold tracking-tight text-4xl mt-3 text-[#1A3622]">
              {formatRange(50, 150, currency, rates)}
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#5C665F]">
              For anything beyond a 15-minute tweak — a new section, a new page, or a fresh
              feature. You always get a fixed quote first.
            </p>
            <ul className="mt-5 space-y-2 text-[15px] text-[#1C211E]">
              <li>– New section or page</li>
              <li>– Design refresh</li>
              <li>– New feature integration</li>
            </ul>
            <p className="mt-auto pt-6 text-[13px] text-[#5C665F]">
              Only pay when something new is actually needed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
